import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServiceMetadata } from '@/modules/Service/services'
import type { ServiceInformationProps } from '@/modules/Service/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    createOtherStandardColumns,
    type OtherStandardPaymentRange,
    type OtherStandardRow,
    type OtherStandardSchedule,
} from './other-standard-config'

const OTHER_STANDARD_METADATA_KEY = 'other_standard_payment_schedule'

const getOtherStandardSchedule = (metadata?: Record<string, any>) => {
    if (!metadata) return undefined

    const schedule =
        metadata[OTHER_STANDARD_METADATA_KEY] ||
        metadata.payment_schedule ||
        metadata.other_standard

    return schedule as OtherStandardSchedule | undefined
}

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation('services')
    const { data: metadata, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_METADATA, service.service_id],
        queryFn: () => getServiceMetadata(service.service_id),
    })

    const columns = useMemo(() => createOtherStandardColumns(t), [t])

    const rows = useMemo<OtherStandardRow[]>(() => {
        const schedule = getOtherStandardSchedule(
            metadata?.metadata as Record<string, any> | undefined
        )
        const fallbackRanges: OtherStandardPaymentRange[] = [
            {
                start_date:
                    service.current_startdate ||
                    service.first_startdate ||
                    service.service_start_date?.toString(),
                end_date: service.current_enddate || service.first_enddate,
                price: service.service_price_register,
            },
        ].filter((range) => range.start_date || range.end_date)

        return [
            {
                ...service,
                description: schedule?.description,
                payment_ranges:
                    schedule?.ranges && schedule.ranges.length > 0
                        ? schedule.ranges
                        : fallbackRanges,
            },
        ]
    }, [metadata, service])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <DataTableCustomized<OtherStandardRow>
            columns={columns}
            rows={rows}
            rowKey={(row) => String(row?.service_id)}
            size="small"
            resizable
            emptyMessage={t('no-data', { defaultValue: 'No data' })}
        />
    )
}

export default Information
